import { useEffect, useState } from 'react'

/** A user-facing error surfaced in the global error popup. */
export interface AppError {
  id: string
  message: string
  detail?: string
}

type Listener = (errors: AppError[]) => void

let errors: AppError[] = []
let seq = 0
const listeners = new Set<Listener>()

function emit(): void {
  for (const l of listeners) l(errors)
}

/**
 * Report an error from anywhere (API layer, event handlers, uncaught errors). It is shown in a
 * centered popup. Identical still-open errors are collapsed so retries/loops don't spam the user.
 */
export function reportError(message: string, detail?: string): void {
  const msg = (message || 'Something went wrong').trim()
  if (errors.some((e) => e.message === msg && e.detail === detail)) return
  errors = [...errors, { id: `err${++seq}`, message: msg, ...(detail ? { detail } : {}) }]
  emit()
}

export function dismissError(id: string): void {
  errors = errors.filter((e) => e.id !== id)
  emit()
}

export function clearErrors(): void {
  errors = []
  emit()
}

function subscribe(l: Listener): () => void {
  listeners.add(l)
  l(errors)
  return () => {
    listeners.delete(l)
  }
}

/** React view of the current error queue. */
export function useErrors(): AppError[] {
  const [list, setList] = useState<AppError[]>(errors)
  useEffect(() => subscribe(setList), [])
  return list
}

/**
 * A failure that is already taken care of: the backend could not be reached, and api.ts#request
 * has either put the app in "Reconnecting…" (a background read) or said so once itself (a member
 * action). Tagged on the error it throws, so a caller that lets it escape uncaught does not add an
 * "Unexpected error" dialog on top.
 */
export function markTransient<E extends Error>(error: E): E & { transient: true } {
  return Object.assign(error, { transient: true as const })
}

export function isTransientError(e: unknown): boolean {
  return typeof e === 'object' && e !== null && (e as { transient?: unknown }).transient === true
}

let installed = false

/** Install catch-all handlers for uncaught promise rejections and runtime errors (once). */
export function installGlobalErrorHandlers(): void {
  if (installed || typeof window === 'undefined') return
  installed = true
  window.addEventListener('unhandledrejection', (e) => {
    const reason = e.reason
    const msg = reason instanceof Error ? reason.message : String(reason)
    if (msg === 'unauthenticated') return // handled by the auth flow — not a user popup
    if (isTransientError(reason)) return // handled by the connectivity store — see markTransient
    reportError('Unexpected error', msg)
  })
  window.addEventListener('error', (e) => {
    if (e.error instanceof Error) reportError('Unexpected error', e.error.message)
  })
}
